import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from './NotificationBell';
import { getNavigationForRole } from '../../config/navigation';
import { getNavIcon, ROLE_LABELS, UI_ICONS } from '../../config/navIcons';
import { BRAND_NAME, BRAND_LOGO } from '../../config/brand';
import ElevenLabsAgentWidget from '../common/ElevenLabsAgentWidget';

const SIDEBAR_KEY = 'nekta_sidebar_collapsed';

/* ─── Admin dark sidebar style tokens ────────────────────── */
const ADMIN_BG        = '#132b1c';   // dark forest green
const ADMIN_BORDER    = 'rgba(255,255,255,0.08)';
const ADMIN_TEXT      = 'rgba(255,255,255,0.82)';
const ADMIN_MUTED     = 'rgba(255,255,255,0.40)';
const ADMIN_HOVER_BG  = 'rgba(255,255,255,0.07)';
const ADMIN_ACTIVE_BG = 'rgba(255,255,255,0.13)';
const ADMIN_ACTIVE_TXT= '#ffffff';

const Layout = ({ children }) => {
    const { user, logout, navRole, hasPermission } = useAuth();
    const navigate  = useNavigate();
    const location  = useLocation();
    const isAdmin   = navRole === 'admin';

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem(SIDEBAR_KEY) === '1'; }
        catch { return false; }
    });

    const navigation = useMemo(
        () => getNavigationForRole(navRole).filter((item) => {
            if (!item.permission) return true;
            return hasPermission(item.permission.resource, item.permission.action);
        }),
        [navRole, hasPermission]
    );

    const shortcuts = navigation.filter((item) => item.shortcut).slice(0, 5);
    const roleLabel = ROLE_LABELS[navRole] || 'Member';
    const initials  = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase() || 'U';

    useEffect(() => {
        const onKeyDown = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        document.body.style.overflowX = 'hidden';
        return () => { document.body.style.overflowX = ''; };
    }, []);

    useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

    useEffect(() => {
        try { localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0'); }
        catch { /* ignore */ }
    }, [collapsed]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const [openGroups, setOpenGroups] = useState(() => {
        try { return JSON.parse(localStorage.getItem('nekta_nav_open_groups') || '[]'); }
        catch { return []; }
    });

    useEffect(() => {
        try { localStorage.setItem('nekta_nav_open_groups', JSON.stringify(openGroups)); }
        catch { /* ignore */ }
    }, [openGroups]);

    const toggleGroup = (name) => setOpenGroups((prev) =>
        prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );

    useEffect(() => {
        const activeChild = navigation.find((item) =>
            item.children?.some((child) =>
                location.pathname === child.href || location.pathname.startsWith(child.href + '/')
            )
        );
        if (activeChild && !openGroups.includes(activeChild.name)) {
            setOpenGroups((prev) => [...prev, activeChild.name]);
        }
    }, [location.pathname, navigation, openGroups]);

    /* ── Nav item renderers ─────────────────────────────── */
    const renderNavItem = (item, { compact = false } = {}) => {
        // Section header label (admin sidebar grouping)
        if (item.section) {
            if (compact) return null;
            return (
                <p key={item.section} style={{
                    margin: '10px 0 2px',
                    padding: '0 10px',
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: isAdmin ? 'rgba(255,255,255,0.32)' : '#8fa09e',
                    userSelect: 'none',
                }}>{item.section}</p>
            );
        }

        const Icon = getNavIcon(item.href);

        if (item.children && !compact) {
            const isOpen      = openGroups.includes(item.name);
            const childActive = item.children.some(
                (child) => location.pathname === child.href || location.pathname.startsWith(child.href + '/')
            );
            if (isAdmin) {
                return (
                    <div key={item.name} className="nav-group">
                        <button
                            type="button"
                            onClick={() => toggleGroup(item.name)}
                            aria-expanded={isOpen}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                                padding: '7px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                background: childActive ? ADMIN_ACTIVE_BG : 'transparent',
                                color: childActive ? ADMIN_ACTIVE_TXT : ADMIN_TEXT,
                                fontSize: 13, fontWeight: childActive ? 600 : 400, textAlign: 'left',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => { if (!childActive) e.currentTarget.style.background = ADMIN_HOVER_BG; }}
                            onMouseLeave={e => { if (!childActive) e.currentTarget.style.background = 'transparent'; }}
                        >
                            <Icon style={{ width: 16, height: 16, flexShrink: 0, opacity: 0.8 }} />
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                            <UI_ICONS.chevronDown style={{ width: 14, height: 14, flexShrink: 0, opacity: 0.5, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </button>
                        {isOpen && (
                            <div style={{ marginLeft: 24, paddingLeft: 8, borderLeft: `1px solid ${ADMIN_BORDER}`, marginTop: 2 }}>
                                {item.children.map((child) => {
                                    const ChildIcon    = getNavIcon(child.href);
                                    const childIsActive = location.pathname === child.href || location.pathname.startsWith(child.href + '/');
                                    return (
                                        <NavLink
                                            key={`${child.name}-${child.href}`}
                                            to={child.href}
                                            end={['/profile','/journey','/hours','/learning','/tasks'].includes(child.href)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 7,
                                                padding: '6px 8px', borderRadius: 6, marginBottom: 2,
                                                fontSize: 12, fontWeight: childIsActive ? 600 : 400,
                                                color: childIsActive ? ADMIN_ACTIVE_TXT : ADMIN_TEXT,
                                                background: childIsActive ? ADMIN_ACTIVE_BG : 'transparent',
                                                textDecoration: 'none', transition: 'background 0.15s',
                                            }}
                                            onMouseEnter={e => { if (!childIsActive) e.currentTarget.style.background = ADMIN_HOVER_BG; }}
                                            onMouseLeave={e => { if (!childIsActive) e.currentTarget.style.background = childIsActive ? ADMIN_ACTIVE_BG : 'transparent'; }}
                                        >
                                            <ChildIcon style={{ width: 14, height: 14, flexShrink: 0, opacity: 0.7 }} />
                                            <span>{child.name}</span>
                                        </NavLink>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            }
            return (
                <div key={item.name} className="nav-group">
                    <button
                        type="button"
                        onClick={() => toggleGroup(item.name)}
                        aria-expanded={isOpen}
                        className={`nav-link group w-full ${childActive ? 'nav-link-active' : ''}`}
                    >
                        <Icon className={`h-[18px] w-[18px] shrink-0 transition-transform duration-200 group-hover:scale-105 ${childActive ? 'text-brand-700' : 'text-ink-500 group-hover:text-ink-800'}`} aria-hidden="true" />
                        <span className="flex min-w-0 flex-1 items-center gap-1.5">
                            <span className="truncate">{item.name}</span>
                        </span>
                        <UI_ICONS.chevronDown className={`h-4 w-4 shrink-0 text-ink-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                    </button>
                    {isOpen && (
                        <div id={`nav-group-${item.name.replace(/\s+/g, '-').toLowerCase()}`} className="mt-0.5 ml-[22px] space-y-0.5 border-l border-border pl-2">
                            {item.children.map((child) => {
                                const ChildIcon = getNavIcon(child.href);
                                return (
                                    <NavLink
                                        key={`${child.name}-${child.href}`}
                                        to={child.href}
                                        end={['/profile','/journey','/hours','/learning','/tasks'].includes(child.href)}
                                        className={({ isActive }) => `nav-link group !px-2 !py-1.5 text-[13px] ${isActive ? 'nav-link-active' : ''}`}
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <ChildIcon className={`h-4 w-4 shrink-0 ${isActive ? 'text-brand-700' : 'text-ink-500'}`} aria-hidden="true" />
                                                <span className="truncate">{child.name}</span>
                                            </>
                                        )}
                                    </NavLink>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        if (item.children && compact) {
            const flatItem = { name: item.name, href: item.href, status: item.status };
            return renderFlatNavItem(flatItem, { compact });
        }
        return renderFlatNavItem(item, { compact });
    };

    const renderFlatNavItem = (item, { compact = false } = {}) => {
        const Icon = getNavIcon(item.href);
        // Extra paths that also highlight this link (e.g. one "Projects" link for two tabs).
        const alsoActive = (item.activePaths || []).some(
            (path) => location.pathname === path || location.pathname.startsWith(path + '/')
        );
        if (isAdmin) {
            return (
                <NavLink
                    key={`${item.name}-${item.href}`}
                    to={item.href}
                    end={item.href === '/admin/dashboard'}
                    onClick={() => setSidebarOpen(false)}
                    title={compact ? item.name : undefined}
                    style={({ isActive: routeActive }) => {
                        const isActive = routeActive || alsoActive;
                        return {
                            display: 'flex', alignItems: 'center',
                            gap: compact ? 0 : 8,
                            justifyContent: compact ? 'center' : 'flex-start',
                            padding: compact ? '8px 6px' : '7px 10px',
                            borderRadius: 8, marginBottom: 2,
                            fontSize: 13,
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? ADMIN_ACTIVE_TXT : ADMIN_TEXT,
                            background: isActive ? ADMIN_ACTIVE_BG : 'transparent',
                            textDecoration: 'none', transition: 'background 0.15s',
                        };
                    }}
                    onMouseEnter={e => {
                        const isActive = alsoActive || e.currentTarget.getAttribute('aria-current') === 'page';
                        if (!isActive) e.currentTarget.style.background = ADMIN_HOVER_BG;
                    }}
                    onMouseLeave={e => {
                        const isActive = alsoActive || e.currentTarget.getAttribute('aria-current') === 'page';
                        e.currentTarget.style.background = isActive ? ADMIN_ACTIVE_BG : 'transparent';
                    }}
                >
                    <Icon style={{ width: 16, height: 16, flexShrink: 0, opacity: 0.8 }} aria-hidden="true" />
                    {!compact && <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>}
                </NavLink>
            );
        }

        return (
            <NavLink
                key={`${item.name}-${item.href}`}
                to={item.href}
                end={item.href === '/dashboard' || item.href === '/admin/dashboard' || item.href === '/mentor/dashboard'}
                onClick={() => setSidebarOpen(false)}
                title={compact ? item.name : undefined}
                className={({ isActive }) => ['nav-link group', compact ? 'justify-center px-2' : '', isActive || alsoActive ? 'nav-link-active' : ''].join(' ')}
            >
                {({ isActive }) => (
                    <>
                        <Icon className={`h-[18px] w-[18px] shrink-0 transition-transform duration-200 group-hover:scale-105 ${isActive || alsoActive ? 'text-brand-700' : 'text-ink-500 group-hover:text-ink-800'}`} aria-hidden="true" />
                        {!compact && (
                            <span className="flex min-w-0 flex-1 items-center gap-1.5">
                                <span className="truncate">{item.name}</span>
                                {item.status === 'coming-soon' && (
                                    <span className="badge badge-neutral shrink-0 !px-1 !py-0 !text-[9px]">Soon</span>
                                )}
                            </span>
                        )}
                    </>
                )}
            </NavLink>
        );
    };

    /* ── Brand block ────────────────────────────────────── */
    const brandBlock = (compact, { showCollapse = false } = {}) => {
        if (isAdmin) {
            return (
                <div style={{
                    display: 'flex', flexShrink: 0,
                    flexDirection: compact ? 'column' : 'row',
                    alignItems: 'center',
                    justifyContent: compact ? 'center' : 'space-between',
                    gap: compact ? 4 : 8,
                    padding: compact ? '10px 6px' : '12px 14px',
                    borderBottom: `1px solid ${ADMIN_BORDER}`,
                    minHeight: 52,
                }}>
                    {!compact ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            <img src={BRAND_LOGO} alt={BRAND_NAME} style={{ height: 26, width: 'auto', maxWidth: 100, objectFit: 'contain' }} />
                            <div style={{ minWidth: 0 }}>
                                <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#ffffff', letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>LancersTech</p>
                                <p style={{ margin: '1px 0 0', fontSize: 9.5, color: ADMIN_MUTED, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Admin Portal</p>
                            </div>
                        </div>
                    ) : (
                        <img src={BRAND_LOGO} alt={BRAND_NAME} style={{ height: 20, width: 'auto', maxWidth: 36, objectFit: 'contain' }} />
                    )}
                    {showCollapse && (
                        <button
                            type="button"
                            onClick={() => setCollapsed((v) => !v)}
                            aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: ADMIN_MUTED, padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center' }}
                        >
                            {compact
                                ? <UI_ICONS.chevronRight style={{ width: 14, height: 14 }} />
                                : <UI_ICONS.chevronLeft  style={{ width: 14, height: 14 }} />
                            }
                        </button>
                    )}
                </div>
            );
        }
        return (
            <div className={`flex shrink-0 border-b border-border ${compact ? 'flex-col items-center justify-center gap-1 py-2 px-1' : 'h-14 items-center justify-between gap-2 px-3'}`}>
                {!compact ? (
                    <div className="flex items-center gap-2.5 min-w-0">
                        <img src={BRAND_LOGO} alt={BRAND_NAME} className="h-7 w-auto max-w-[100px] object-contain" />
                        <div className="min-w-0">
                            <p className="truncate text-[13px] font-bold tracking-tight text-ink-950">LancersTech</p>
                            <p className="truncate text-[10px] font-medium text-ink-500">{roleLabel} Portal</p>
                        </div>
                    </div>
                ) : (
                    <img src={BRAND_LOGO} alt={BRAND_NAME} className="h-6 w-auto max-w-[36px] object-contain my-1" />
                )}
                {showCollapse && (
                    <button type="button" className="btn-icon !h-7 !w-7 shrink-0" aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'} onClick={() => setCollapsed((v) => !v)}>
                        {compact ? <UI_ICONS.chevronRight className="h-4 w-4" /> : <UI_ICONS.chevronLeft className="h-4 w-4" />}
                    </button>
                )}
            </div>
        );
    };

    /* ── User block ─────────────────────────────────────── */
    const userBlock = (compact) => {
        if (isAdmin) {
            return (
                <div style={{ flexShrink: 0, borderTop: `1px solid ${ADMIN_BORDER}`, padding: compact ? 6 : 10 }}>
                    {!compact && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, marginBottom: 6 }}>
                            <div style={{ width: 28, height: 28, flexShrink: 0, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>
                                {initials}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {user?.first_name} {user?.last_name}
                                </p>
                                <p style={{ margin: 0, fontSize: 10, color: ADMIN_MUTED }}>{roleLabel}</p>
                            </div>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={handleLogout}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            justifyContent: compact ? 'center' : 'flex-start',
                            width: '100%', padding: compact ? '8px 6px' : '7px 10px',
                            borderRadius: 8, border: 'none', cursor: 'pointer',
                            background: 'transparent', color: 'rgba(255,120,100,0.85)',
                            fontSize: 13, fontWeight: 500, transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,80,60,0.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        aria-label="Log out"
                    >
                        <UI_ICONS.logout style={{ width: 16, height: 16 }} />
                        {!compact && <span>Log out</span>}
                    </button>
                </div>
            );
        }
        return (
            <div className={`shrink-0 border-t border-border ${compact ? 'p-1.5' : 'p-2'}`}>
                {!compact && (
                    <div className="mb-2 flex items-center gap-2 rounded-md bg-ink-50 px-2 py-2">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-800">{initials}</div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-semibold text-ink-950">{user?.first_name} {user?.last_name}</p>
                            <p className="truncate text-[10px] text-ink-500">{roleLabel}</p>
                        </div>
                    </div>
                )}
                <button type="button" onClick={handleLogout} className={`nav-link w-full text-red-700 hover:bg-red-50 hover:text-red-800 ${compact ? 'justify-center px-2' : ''}`} aria-label="Log out">
                    <UI_ICONS.logout className="h-5 w-5" aria-hidden="true" />
                    {!compact && <span>Log out</span>}
                </button>
            </div>
        );
    };

    const sidebarWidth = collapsed ? 'lg:w-[4.25rem]' : 'lg:w-[13.5rem]';
    const mainPad      = collapsed ? 'lg:pl-[4.25rem]' : 'lg:pl-[13.5rem]';
    const adminSidebarBg = isAdmin ? ADMIN_BG : '#ffffff';

    return (
        <div className="app-shell overflow-x-hidden">
            {/* Mobile drawer */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-40 lg:hidden">
                    <div className="fixed inset-0 bg-ink-950/40 backdrop-blur-[2px] animate-fade-in" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
                    <div
                        className="fixed inset-y-0 left-0 flex w-[14.5rem] flex-col shadow-soft animate-slide-in-left"
                        style={{ background: adminSidebarBg }}
                        role="dialog" aria-modal="true" aria-label="Navigation menu"
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${isAdmin ? ADMIN_BORDER : '#e4e8e7'}`, paddingRight: 8 }}>
                            {brandBlock(false)}
                            <button type="button" className={isAdmin ? '' : 'btn-icon'} style={isAdmin ? { background: 'transparent', border: 'none', cursor: 'pointer', color: ADMIN_MUTED, padding: 6 } : {}} aria-label="Close navigation" onClick={() => setSidebarOpen(false)}>
                                <UI_ICONS.close style={{ width: 18, height: 18 }} aria-hidden="true" />
                            </button>
                        </div>
                        <nav id="mobile-primary-nav" className="flex-1 space-y-0.5 overflow-y-auto px-1.5 py-2" aria-label="Primary">
                            {navigation.map((item) => renderNavItem(item))}
                        </nav>
                        {userBlock(false)}
                    </div>
                </div>
            )}

            {/* Desktop sidebar */}
            <aside
                className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col ${sidebarWidth} transition-[width] duration-200`}
                style={{ background: adminSidebarBg, borderRight: `1px solid ${isAdmin ? ADMIN_BORDER : '#e4e8e7'}` }}
            >
                {brandBlock(collapsed, { showCollapse: true })}
                <nav
                    id="desktop-primary-nav"
                    className="min-h-0 flex-1 overflow-y-auto py-2"
                    style={{ padding: '8px 6px' }}
                    aria-label="Primary"
                >
                    {navigation.map((item) => renderNavItem(item, { compact: collapsed }))}
                </nav>
                {userBlock(collapsed)}
            </aside>

            <div className={`${mainPad} transition-[padding] duration-200`}>
                <header className="sticky top-0 z-10 border-b border-border bg-white/90 backdrop-blur-md">
                    <div className="flex h-14 items-center justify-between gap-3 px-4 md:px-6">
                        <div className="flex items-center gap-2 lg:hidden">
                            <button type="button" className="btn-icon" aria-label="Open navigation" aria-expanded={sidebarOpen} aria-controls="mobile-primary-nav" onClick={() => setSidebarOpen(true)}>
                                <UI_ICONS.menu className="h-5 w-5" aria-hidden="true" />
                            </button>
                            <div className="flex items-center gap-2">
                                <img src={BRAND_LOGO} alt={BRAND_NAME} className="h-6 w-auto max-w-[90px] object-contain" />
                                <span className="text-sm font-bold text-ink-900">{isAdmin ? 'Admin Portal' : 'Talent Portal'}</span>
                            </div>
                        </div>

                        <div className="hidden lg:block">
                            <p className="text-sm font-semibold text-ink-800">{roleLabel} workspace</p>
                            <p className="text-xs text-ink-500">Internship command center</p>
                        </div>

                        <div className="ml-auto flex items-center gap-2 sm:gap-3">
                            <NotificationBell />
                            <div className="hidden sm:flex items-center gap-2 rounded-lg border border-border bg-ink-50 px-2.5 py-1.5">
                                <div className={isAdmin ? '' : 'flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-800'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '50%', background: isAdmin ? ADMIN_BG : '#d1f0e8', fontSize: 11, fontWeight: 700, color: isAdmin ? '#fff' : '#0f5257', flexShrink: 0 }}>
                                    {initials}
                                </div>
                                <div className="min-w-0 pr-1">
                                    <p className="truncate text-sm font-semibold text-ink-900 max-w-[140px]">
                                        {user?.first_name} {user?.last_name}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <main className={`page-container ${navRole === 'intern' ? 'pb-24 lg:pb-8' : ''}`}>
                    {children}
                </main>
            </div>

            {navRole === 'intern' && shortcuts.length > 0 && (
                <nav className="lg:hidden fixed bottom-0 inset-x-0 z-20 border-t border-border bg-white/95 backdrop-blur-md" aria-label="Student shortcuts">
                    <ul className="grid grid-cols-5">
                        {shortcuts.map((item) => {
                            const active = location.pathname === item.href;
                            const Icon   = getNavIcon(item.href);
                            const shortName = { Home: 'Home', 'Talent Profile': 'Profile', 'Practical Hours': 'Hours', 'Learning & Training': 'Learn' }[item.name] || item.name;
                            return (
                                <li key={item.href}>
                                    <Link to={item.href} aria-current={active ? 'page' : undefined}
                                        className={`flex flex-col items-center justify-center min-h-[60px] gap-0.5 px-1 text-[10px] font-medium transition-colors ${active ? 'text-brand-800 bg-brand-50' : 'text-ink-500'}`}>
                                        <Icon className={`h-5 w-5 ${active ? 'text-brand-700' : ''}`} aria-hidden="true" />
                                        {shortName}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            )}
            <ElevenLabsAgentWidget />
        </div>
    );
};

export default Layout;
